import {
  FolderFilled,
  FolderOpenOutlined,
  FolderAddOutlined,
  FileAddOutlined,
  FileTextOutlined,
  SwapOutlined,
  FilterFilled,
} from "@ant-design/icons";
import { Switch, Tooltip, Tag, Checkbox, DatePicker } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  TableButton,
  TableSearchFilter,
  MultipleOptionsTableFilter,
  RangeSlider,
} from "shared/ui";
import {
  TASK_TRIGGER_ON_EVENT,
  TASK_TRIGGER_PERIODICALLY,
  TaskTrigger,
} from "shared/api";
import {
  FlatSection,
  SectionRow,
  TaskHubTreeRow,
  TaskRow,
  UserSelectOption,
} from "../../pages/ProductCards/ProductCards/ui/TaskHub/types";
import {
  formatPeriodicity,
  getTimezone,
} from "../../pages/ProductCards/ProductCards/ui/TaskHub/taskHubHelpers";
import { MoveToMenu } from "../../pages/ProductCards/ProductCards/ui/TaskHub/MoveToMenu";
import { useTaskHubFilters } from "./useTaskHubFilters";

interface UseTaskHubTreeColumnsProps {
  flatSections: FlatSection[];
  /** Section ids (self + descendants) into which a given section may NOT move. */
  getDisallowedSectionIds: (sectionId: number) => Set<number>;
  onAddTaskInSection: (sectionId: number) => void;
  onAddSubsection: (parentSectionId: number) => void;
  onEditSection: (section: SectionRow) => void;
  onDeleteSection: (id: number) => void;
  onMoveSection: (id: number, targetSectionId: number | null) => void;
  onEditTask: (id: number) => void;
  onDeleteTask: (id: number) => void;
  onDuplicateTask: (task: TaskRow) => void;
  onMoveTask: (id: number, targetSectionId: number | null) => void;
  onToggleTask: (task: TaskRow) => void;
  /** Server-side filter state shared with the section_hierarchy query. */
  filters: ReturnType<typeof useTaskHubFilters>;
  transformedUsers: UserSelectOption[];
}

const getEnabledFilterString = (val: boolean | undefined) => {
  if (val === true) return "true";
  if (val === false) return "false";
  return "";
};

export const useTaskHubTreeColumns = ({
  flatSections,
  getDisallowedSectionIds,
  onAddTaskInSection,
  onAddSubsection,
  onEditSection,
  onDeleteSection,
  onMoveSection,
  onEditTask,
  onDeleteTask,
  onDuplicateTask,
  onMoveTask,
  onToggleTask,
  filters,
  transformedUsers,
}: UseTaskHubTreeColumnsProps): ColumnsType<TaskHubTreeRow> => {
  const { t } = useTranslation();
  const timezone = getTimezone();

  const rootLabel = t("taskHub.sections.rootLevel") || "Top Level";
  const moveToLabel = t("taskHub.sections.moveTo") || "Move to";

  return [
    {
      title: t("taskHub.table.name") || "Name",
      key: "name",
      filterIcon: (
        <FilterFilled
          className={
            filters.taskNameValue.length
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
      filterDropdown: ({ setSelectedKeys, confirm, visible }) => (
        <TableSearchFilter
          setSelectedKeys={setSelectedKeys}
          clearFilters={() => filters.handleTaskNameValue("")}
          confirm={confirm}
          value={filters.taskNameValue}
          handleFilterChange={filters.handleTaskNameValue}
          visible={visible}
          placeholder={t("taskHub.searchByName") || "Search by name"}
          title={t("Name") || ""}
        />
      ),
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType === "section") {
          const isEmpty = row.taskCount === 0 && row.sectionCount === 0;
          return (
            <span className="flex items-center gap-2">
              <FolderFilled className="text-yellow-500 text-base" />
              <span className="font-semibold">{row.name}</span>

              {isEmpty ? (
                <Tag className="!m-0 !text-gray-400 !bg-gray-50 !border-gray-200">
                  {t("taskHub.sections.empty") || "empty"}
                </Tag>
              ) : (
                <span className="flex items-center gap-1">
                  {row.sectionCount > 0 && (
                    <Tooltip
                      title={
                        t("taskHub.sections.subsectionsTooltip") ||
                        "Subsections"
                      }
                    >
                      <Tag
                        color="gold"
                        className="!m-0 !flex !items-center gap-1"
                      >
                        <FolderOpenOutlined />
                        {row.sectionCount}
                      </Tag>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={t("taskHub.sections.tasksTooltip") || "Tasks inside"}
                  >
                    <Tag
                      color="blue"
                      className="!m-0 !flex !items-center gap-1"
                    >
                      <FileTextOutlined />
                      {row.taskCount}
                    </Tag>
                  </Tooltip>
                </span>
              )}
            </span>
          );
        }
        return (
          <span className="flex items-center gap-2">
            {row.icon ? (
              <img
                src={row.icon}
                alt="icon"
                style={{ width: 20, height: 20, objectFit: "contain" }}
              />
            ) : null}
            {row.name}
          </span>
        );
      },
    },
    {
      title: t("taskHub.table.users") || "Users",
      key: "users",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType !== "task") return null;
        const labels = (row.users ?? [])
          .map((u) => u.user?.username)
          .filter(Boolean);
        return labels.length ? (
          <span>{labels.join(", ")}</span>
        ) : (
          <span>N/A</span>
        );
      },
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px]">
          <Checkbox.Group
            className="flex flex-col items-start space-y-1 max-h-[200px] overflow-x-hidden overflow-y-auto"
            value={filters.userIds}
            onChange={(checkedValues) => {
              filters.setUserIds(checkedValues as number[]);
              confirm();
            }}
          >
            {transformedUsers.map((user) => (
              <Checkbox key={user.value} value={user.value}>
                {user.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            filters.userIds.length > 0 ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.periodicity") || "Periodicity",
      key: "periodicity",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType !== "task") return null;
        if (!row.periodicity) return "-";
        return (
          <span>
            {formatPeriodicity(t, row.periodicity, row.periodicity_multiple)}
          </span>
        );
      },
      filterDropdown: ({ clearFilters }) => (
        <MultipleOptionsTableFilter
          options={[
            { label: t("taskHub.modal.daily") || "Daily", value: "daily" },
            { label: t("taskHub.modal.weekly") || "Weekly", value: "weekly" },
            {
              label: t("taskHub.modal.monthly") || "Monthly",
              value: "monthly",
            },
            { label: t("taskHub.modal.yearly") || "Yearly", value: "yearly" },
          ]}
          selectedValues={
            filters.periodicityValue ? [filters.periodicityValue] : []
          }
          handleChange={([value]) => {
            const val = String(value || "");
            filters.handlePeriodicityValue(
              filters.periodicityValue === val ? "" : val
            );
          }}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: (
        <FilterFilled
          className={
            filters.periodicityValue ? "!text-blue-600 scale-125" : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.trigger") || "Trigger",
      key: "trigger",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType !== "task") return null;
        return row.trigger ? row.trigger : "-";
      },
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[240px]">
          <Checkbox.Group
            className="flex flex-col items-start space-y-1"
            value={filters.triggerInFilter}
            onChange={(checkedValues) => {
              filters.setTriggerInFilter(checkedValues as TaskTrigger[]);
              confirm();
            }}
          >
            <Checkbox value={TASK_TRIGGER_ON_EVENT}>
              {TASK_TRIGGER_ON_EVENT}
            </Checkbox>
            <Checkbox className="!ml-0" value={TASK_TRIGGER_PERIODICALLY}>
              {TASK_TRIGGER_PERIODICALLY}
            </Checkbox>
          </Checkbox.Group>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            filters.triggerInFilter.length > 0
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.priority") || "Priority",
      key: "priority",
      render: (_: unknown, row: TaskHubTreeRow) =>
        row.rowType === "task" ? row.priority : null,
      filterDropdown: () => (
        <RangeSlider
          title="Priority"
          step={1}
          maxValue={5}
          onChange={filters.priorityFilters.onChange}
          onAfterChange={filters.priorityFilters.onAfterChange}
          rangeItemName="Priority"
        />
      ),
      filterIcon: (
        <FilterFilled
          className={classNames({
            "!text-yellow-600 scale-125":
              filters.priorityFilters.isFilterActive(),
          })}
        />
      ),
    },
    {
      title: t("taskHub.table.status") || "Status",
      key: "enabled",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType !== "task") return null;
        return (
          <Switch
            style={{ backgroundColor: row.enabled ? "limegreen" : "tomato" }}
            checked={row.enabled}
            onChange={() => onToggleTask(row)}
          />
        );
      },
      filterDropdown: ({ confirm }) => (
        <div className="p-2 w-[180px]">
          <div className="mb-1">{t("Filter by status")}</div>
          <select
            className="w-full border px-2 py-1 rounded"
            value={getEnabledFilterString(filters.enabledFilter)}
            onChange={(e) => {
              const { value } = e.target;
              if (value === "true") filters.setEnabledFilter(true);
              else if (value === "false") filters.setEnabledFilter(false);
              else filters.setEnabledFilter(undefined);
              confirm();
            }}
          >
            <option value="">{t("All")}</option>
            <option value="true">{t("Enabled")}</option>
            <option value="false">{t("Disabled")}</option>
          </select>
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            filters.enabledFilter !== undefined
              ? "!text-blue-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.start") || "Start",
      key: "start",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType !== "task") return null;
        return (
          <div className="text-sm">
            {row.start_periodicity
              ? dayjs(row.start_periodicity)
                  .tz(timezone)
                  .format("YYYY-MM-DD HH:mm")
              : "-"}
          </div>
        );
      },
      filterDropdown: () => (
        <div className="p-2 w-[240px]">
          <div className="mb-1 text-sm">{t("Start From")}</div>
          <DatePicker
            showTime
            value={
              filters.startDateFilters.dateGreater
                ? dayjs(filters.startDateFilters.dateGreater)
                : null
            }
            onChange={(val) =>
              filters.startDateFilters.handleDateGreater(
                val,
                val?.toISOString() || ""
              )
            }
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <div className="mb-1 text-sm">{t("Start To")}</div>
          <DatePicker
            showTime
            value={
              filters.startDateFilters.dateLesser
                ? dayjs(filters.startDateFilters.dateLesser)
                : null
            }
            onChange={(val) =>
              filters.startDateFilters.handleDateLesser(
                val,
                val?.toISOString() || ""
              )
            }
            format="YYYY-MM-DD HH:mm"
            style={{ width: "100%" }}
          />
        </div>
      ),
      filterIcon: (
        <FilterFilled
          className={
            filters.startDateFilters.isDataFilterActive
              ? "!text-yellow-600 scale-125"
              : undefined
          }
        />
      ),
    },
    {
      title: t("taskHub.table.actions") || "Actions",
      key: "actions",
      render: (_: unknown, row: TaskHubTreeRow) => {
        if (row.rowType === "section") {
          const disallowed = getDisallowedSectionIds(row.id);
          return (
            <div className="flex gap-2 items-center">
              <Tooltip
                title={t("taskHub.sections.addTaskHere") || "Add task here"}
              >
                <FileAddOutlined
                  className="btn-settings"
                  onClick={() => onAddTaskInSection(row.id)}
                />
              </Tooltip>
              <Tooltip
                title={t("taskHub.sections.addSubsection") || "Add subsection"}
              >
                <FolderAddOutlined
                  className="btn-settings"
                  onClick={() => onAddSubsection(row.id)}
                />
              </Tooltip>
              <MoveToMenu
                currentParent={row.parent_section}
                flatSections={flatSections}
                disallowed={disallowed}
                onMove={(target) => onMoveSection(row.id, target)}
                title={moveToLabel}
                rootLabel={rootLabel}
              >
                <Tooltip title={t("taskHub.sections.move") || "Move"}>
                  <SwapOutlined className="btn-additional" />
                </Tooltip>
              </MoveToMenu>
              <TableButton type="edit" onClick={() => onEditSection(row)} />
              <TableButton
                type="delete"
                onClick={() => onDeleteSection(row.id)}
              />
            </div>
          );
        }

        return (
          <div className="flex gap-2 items-center">
            <TableButton type="edit" onClick={() => onEditTask(row.id)} />
            <TableButton
              popoverString={t("taskHub.duplicateTask") || "Duplicate Task"}
              type="clone"
              onClick={() => onDuplicateTask(row)}
            />
            <MoveToMenu
              currentParent={row.parent_section}
              flatSections={flatSections}
              onMove={(target) => onMoveTask(row.id, target)}
              title={moveToLabel}
              rootLabel={rootLabel}
            >
              <Tooltip title={t("taskHub.sections.move") || "Move"}>
                <SwapOutlined className="btn-additional" />
              </Tooltip>
            </MoveToMenu>
            <TableButton type="delete" onClick={() => onDeleteTask(row.id)} />
          </div>
        );
      },
    },
  ];
};
